/**
 * Markdown → HTML for the article preview — a TS port of the whitelabels
 * content engine's renderer (`whitelabels-website/build-tools/content_lib/
 * markdown.py`), emitting the SAME class names so the preview markup doubles as
 * the rendering contract for the casino front-end (`docs/blog-authoring-plan.md`).
 *
 * Dialect: H2-H6 (slugified ids; H2/H3 collected for the ToC), paragraphs,
 * nested bullet/numbered lists (one level), blockquotes, fenced code, `---`,
 * pipe tables (`.data-table` in an overflow wrap), standalone images as
 * captioned figures, `::: tip|warning|note|key` callouts, raw-HTML passthrough
 * for lines starting `<`, and inline strong/em/code/links (`slug:` links
 * resolve by article slug and survive renames).
 *
 * SECURITY: all text content is HTML-escaped before inline markup is applied
 * (the area's escaping-helpers pattern for `dangerouslySetInnerHTML` content —
 * see `src/nova/CLAUDE.md`, the Lumio exception). The raw-HTML passthrough
 * mirrors the wl engine and is author-supplied content, same trust model as
 * there.
 */

export type ArticleHeading = { id: string; text: string };

export type RenderedArticleBody = {
  html: string;
  /** H2/H3 headings in order (ToC). */
  headings: ArticleHeading[];
  wordCount: number;
};

export type CalloutLabels = { tip: string; warning: string; note: string; key: string };

export type RenderOptions = {
  /** Resolve a `slug:` internal link to an href; undefined → dead link (#). */
  resolveSlug: (slug: string) => string | undefined;
  /** Localized callout labels (wl defaults: Tip / Heads up / Note / Key point). */
  calloutLabels: CalloutLabels;
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * Character references + C0 controls resolved the way a browser resolves them
 * when it parses an attribute. Any scheme test has to run on THIS, not on the
 * source text: `javascrip&#116;:` and `java&#9;script:` both reach the parser
 * as `javascript:`.
 */
const decodeEntities = (value: string): string =>
  value
    .replace(/&#x([0-9a-f]+);?/gi, (_m, hex: string) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/&#(\d+);?/g, (_m, dec: string) => String.fromCharCode(parseInt(dec, 10)))
    .replace(/&colon;/gi, ':')
    .replace(/&tab;/gi, '\t')
    .replace(/&newline;/gi, '\n');

/**
 * The scheme-test form: entities resolved AND every space/C0 control removed,
 * because browsers strip those from a scheme before parsing it (`java\tscript:`
 * runs). Only for URL values — stripping spaces would weld a tag name to its
 * first attribute (`<a href=` → `<ahref=`), so tag/attribute scanning uses
 * `decodeEntities` instead.
 */
const decodeForSchemeTest = (value: string): string =>
  decodeEntities(value)
    .split('')
    .filter((ch) => ch.charCodeAt(0) > 0x20)
    .join('');

/**
 * Inline raster images the editor produces itself: the Insert-image button
 * embeds the picked file as a base64 `data:` URL (MarkdownEditor's
 * `readFileAsDataUrl` + `FigureImage({ allowBase64: true })`), so rejecting
 * `data:` outright would render every editor-inserted body image as `src="#"`.
 *
 * Raster types ONLY — `image/svg+xml` is deliberately absent: an SVG document
 * can carry `<script>` and executes when it is the document, so allowing it
 * would reopen the hole this allowlist exists to close.
 */
const SAFE_DATA_IMAGE = /^data:image\/(png|jpe?g|gif|webp|avif);base64,[a-z0-9+/=\s]*$/i;

/**
 * Scheme allowlist for any author-supplied URL that becomes an `href`/`src`.
 *
 * `escapeHtml` is NOT a URL guard: `javascript:alert(1)` contains none of the
 * characters it escapes, so an escaped href is still a live script URL once the
 * markup reaches `dangerouslySetInnerHTML`. Entity-encoded schemes
 * (`javascrip&#116;:`) decode in the browser AFTER any source-text filter runs,
 * so the check has to happen on the decoded value — hence the manual decode of
 * numeric/named references before parsing, and the rejection of any control or
 * whitespace character, which browsers strip from a scheme.
 *
 * Relative URLs are kept (the dialect allows them); anything that does not
 * resolve to http/https/mailto — or does not parse at all — collapses to '#'.
 */
export const safeUrl = (value: string): string => {
  const decoded = decodeForSchemeTest(value);

  // No scheme at all → relative path/anchor, which cannot execute script.
  if (!/^[a-z][a-z0-9+.-]*:/i.test(decoded)) return value;

  if (SAFE_DATA_IMAGE.test(decoded)) return value;

  try {
    const { protocol } = new URL(decoded);
    return protocol === 'http:' || protocol === 'https:' || protocol === 'mailto:' ? value : '#';
  } catch {
    return '#';
  }
};

export const slugifyHeading = (text: string): string =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/** The NUL sentinel guarding stashed HTML snippets (cannot occur in copy). */
const SENTINEL = String.fromCharCode(0);

/** strong/em passes, shared by body text and link labels. */
const emStrong = (text: string): string =>
  text
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[\s(])\*([^*\s][^*]*)\*/g, '$1<em>$2</em>')
    .replace(/(^|[\s(])_([^_\s][^_]*)_/g, '$1<em>$2</em>');

/**
 * Inline markup. Mirrors the wl ordering: code spans, images and links are
 * matched on the RAW string and their parts escaped exactly once at stash time
 * (double-escaping an href turns & into &amp;amp; and breaks the URL); the
 * remaining text is escaped afterwards, then strong/em run over it.
 */
const renderInline = (raw: string, options: RenderOptions): string => {
  let text = raw;
  const stash: string[] = [];
  const keep = (html: string): string => {
    stash.push(html);
    return `${SENTINEL}${stash.length - 1}${SENTINEL}`;
  };

  // Code spans first: their content is literal.
  text = text.replace(/`([^`]+)`/g, (_m, code: string) => keep(`<code>${escapeHtml(code)}</code>`));

  // Inline images (before links: same bracket syntax with a leading !).
  text = text.replace(
    /!\[([^\]]*)\]\(([^()\s]+)(?:\s+"([^"]*)")?\)/g,
    (_m, alt: string, srcUrl: string) =>
      keep(`<img src="${escapeHtml(safeUrl(srcUrl))}" alt="${escapeHtml(alt)}" loading="lazy">`),
  );

  // Links: [text](slug:article) | [text](https://…) | [text](relative).
  // Labels support nested strong/em, like the wl renderer.
  text = text.replace(/\[([^\]]+)\]\(([^()\s]+)\)/g, (_m, label: string, href: string) => {
    const labelHtml = emStrong(escapeHtml(label));
    if (href.startsWith('slug:')) {
      const resolved = options.resolveSlug(href.slice(5));
      return keep(`<a href="${escapeHtml(resolved ?? '#')}">${labelHtml}</a>`);
    }
    if (/^https?:\/\//.test(href)) {
      return keep(`<a href="${escapeHtml(href)}" target="_blank" rel="noopener">${labelHtml}</a>`);
    }
    // Everything else is relative OR another scheme — `escapeHtml` would happily
    // emit a live `javascript:` href, so the scheme allowlist runs first.
    return keep(`<a href="${escapeHtml(safeUrl(href))}">${labelHtml}</a>`);
  });

  text = emStrong(escapeHtml(text));

  // String-based restore (a NUL-sentinel regex literal would trip eslint no-control-regex).
  return stash.reduce((acc, html, index) => acc.split(`${SENTINEL}${index}${SENTINEL}`).join(html), text);
};

/**
 * Tags the raw-HTML passthrough may emit live. An ALLOWLIST, not a blocklist:
 * the previous `<script|javascript:|on\w+=` filter was bypassed by `<base
 * href>` (which silently re-points every relative link on the page, including
 * this page's own ToC anchors) and by `<iframe src="data:text/html;base64,…">`
 * (which executes attacker script on render) — neither contains any of the
 * blocked substrings. Enumerating what is SAFE cannot be defeated by a tag
 * nobody thought of.
 *
 * Formatting and table markup only: nothing that can load a subresource,
 * navigate, change document context, or carry a scripting surface.
 */
const ALLOWED_RAW_TAGS = new Set([
  'p', 'br', 'hr', 'span', 'div', 'blockquote', 'pre', 'code',
  'strong', 'b', 'em', 'i', 'u', 's', 'sub', 'sup', 'small', 'mark',
  'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
  'figure', 'figcaption', 'a', 'img',
]);

/** Attributes that may survive on an allowed tag. `on*` handlers can never match. */
const ALLOWED_RAW_ATTRS = new Set(['href', 'src', 'alt', 'title', 'class', 'colspan', 'rowspan', 'id', 'loading']);

/** The raw-HTML attributes whose value is a URL. */
const RAW_URL_ATTRS = new Set(['href', 'src']);

/**
 * Elements an inline infographic (`::: infographic`) may use. Drawing
 * primitives and text only.
 *
 * Absent on purpose: `image`, `use`, `foreignObject` and `a` (each reaches
 * outside the document or nests arbitrary markup), `script` and every
 * `animate*` element (scripting surfaces), and `style` — an SVG <style> block
 * carries CSS, and CSS carries url() and @import.
 */
const ALLOWED_SVG_TAGS = new Set([
  'svg', 'g', 'defs', 'title', 'desc',
  'path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon',
  'text', 'tspan',
  'lineargradient', 'radialgradient', 'stop', 'clippath',
]);

/**
 * The presentation and geometry attributes those elements may carry.
 *
 * Absent on purpose: `href` and `xlink:href` in every form — with no way to
 * reference anything, an infographic cannot fetch, and `<use href="#…">` tricks
 * have no surface. `style` is absent for the same reason as the tag.
 */
const ALLOWED_SVG_ATTRS = new Set([
  'xmlns', 'viewbox', 'preserveaspectratio',
  'width', 'height', 'x', 'y', 'dx', 'dy',
  'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry',
  'd', 'points', 'transform',
  'fill', 'fill-opacity', 'fill-rule',
  'stroke', 'stroke-width', 'stroke-opacity', 'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray',
  'opacity', 'offset', 'stop-color', 'stop-opacity',
  'gradientunits', 'gradienttransform', 'spreadmethod',
  'clip-path', 'clippathunits',
  'text-anchor', 'dominant-baseline', 'letter-spacing',
  'font-size', 'font-family', 'font-weight', 'font-style',
  'class', 'id', 'role', 'aria-hidden', 'aria-label',
]);

/**
 * True when a block is safe to emit live: every tag allowlisted, every
 * attribute allowlisted, every URL-bearing attribute surviving `safeUrl`, and
 * the whole block parseable as well-formed tags. FAIL-CLOSED — anything this
 * cannot account for means the caller escapes the block instead.
 *
 * Tokenised rather than substring-matched. A previous regex version checked
 * only QUOTED attribute values, so `<a href=javascript:alert(1)>` walked
 * straight through, and its attribute-name scan also fired inside values, so a
 * legitimate `?a=b` query string was rejected. Both are the same root cause:
 * HTML structure cannot be recognised by scanning for substrings.
 */
const TAG_RE = /<\s*\/?\s*([a-z][a-z0-9-]*)((?:[^>"']|"[^"]*"|'[^']*')*)>/gi;
const ATTR_RE = /([a-z][a-z0-9-]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/gi;

const isBlockSafe = (block: string, tags: Set<string>, attrs: Set<string>, urlAttrs: Set<string>): boolean => {
  // Entities resolved, whitespace INTACT: the tag grammar needs the spaces
  // (stripping them would weld `<a href=` into `<ahref=`).
  const decoded = decodeEntities(block);

  for (const tag of decoded.matchAll(TAG_RE)) {
    if (!tags.has(tag[1].toLowerCase())) return false;

    for (const attr of (tag[2] ?? '').matchAll(ATTR_RE)) {
      const name = attr[1].toLowerCase();
      if (!attrs.has(name)) return false;
      if (!urlAttrs.has(name)) continue;
      // attr[2]/[3] are the quoted forms, attr[4] the unquoted one.
      const value = attr[2] ?? attr[3] ?? attr[4] ?? '';
      if (value && safeUrl(value) === '#' && value !== '#') return false;
    }
  }

  // Fail closed on anything tag-shaped the parser could not account for
  // (malformed markup, comments, doctypes, CDATA, stray `</`).
  const leftovers = decoded.split(TAG_RE).join('');
  return !/<\s*[a-z!/?]/i.test(leftovers);
};

const isRawHtmlSafe = (block: string): boolean =>
  isBlockSafe(block, ALLOWED_RAW_TAGS, ALLOWED_RAW_ATTRS, RAW_URL_ATTRS);

/**
 * True when an inline infographic is safe to emit live. No SVG attribute on the
 * allowlist carries a URL, so the URL check has nothing to walk — the guarantee
 * comes from the allowlist alone.
 */
const isSvgSafe = (block: string): boolean =>
  isBlockSafe(block, ALLOWED_SVG_TAGS, ALLOWED_SVG_ATTRS, new Set());

const CALLOUT_KIND_RE = /^:::\s*(tip|warning|note|key)\b/;

/**
 * The engagement blocks. Same `:::` family as callouts and closed the same way,
 * so — same limitation — one cannot nest inside another. Each carries a title
 * on the opening line rather than only a kind.
 */
const ENGAGEMENT_RE = /^:::\s*(toggle|quiz|infographic)\b\s*(.*)$/;

/** A quiz option: `- [ ] wrong` / `- [x] right`. */
const QUIZ_OPTION_RE = /^[-*]\s+\[([ xX])\]\s*(.*)$/;

/** A standalone figure line; shared with the paragraph interrupt so a
 *  non-figure '![' line flows into the paragraph instead of vanishing. */
const FIGURE_RE = /^!\[([^\]]*)\]\(([^()\s]+)(?:\s+"([^"]*)")?\)$/;

type ListItem = { text: string; children: ListItem[] };

export const renderArticleBody = (markdown: string, options: RenderOptions): RenderedArticleBody => {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  const headings: ArticleHeading[] = [];
  const usedIds = new Set<string>();
  let wordCount = 0;

  const uniqueId = (base: string): string => {
    const safe = base || 'section';
    let id = safe;
    let n = 1;
    while (usedIds.has(id)) id = `${safe}-${(n += 1)}`;
    usedIds.add(id);
    return id;
  };

  const countWords = (text: string): void => {
    const words = text.trim().split(/\s+/).filter(Boolean);
    wordCount += words.length;
  };

  const renderList = (items: ListItem[], ordered: boolean): string => {
    const tag = ordered ? 'ol' : 'ul';
    const body = items
      .map((item) => {
        const nested = item.children.length > 0 ? renderList(item.children, false) : '';
        return `<li>${renderInline(item.text, options)}${nested}</li>`;
      })
      .join('');
    return `<${tag} class="post-list">${body}</${tag}>`;
  };

  let i = 0;
  const emitBlock = (html: string): void => {
    out.push(html);
  };

  // The callout body is parsed recursively through the same pipeline; to keep
  // this single-pass, callouts collect their inner lines and re-enter render.
  const renderCalloutBody = (inner: string[]): string => {
    const rendered = renderArticleBody(inner.join('\n'), options);
    wordCount += rendered.wordCount;
    return rendered.html;
  };

  /**
   * The one-question, zero-JavaScript quiz.
   *
   * Radio inputs plus `:checked` sibling rules are the whole mechanism: picking
   * an option reveals its mark and, through `:has()` on the container, the
   * author's explanation. Nothing here runs script, which is what lets the block
   * exist on a public page that ships no JS at all.
   *
   * The right/wrong marks carry NO text — they are painted by the stylesheet.
   * Copy inside the renderer would have to be localized, and the renderer takes
   * its only localized strings (the callout labels) from options; adding a
   * second set for this would put English on every non-English article the day
   * a caller forgot to pass it. The author's explanation, written in the
   * article's own language and revealed on answering, carries the meaning.
   */
  const renderQuiz = (question: string, inner: string[]): string => {
    const quizOptions: Array<{ right: boolean; text: string }> = [];
    const answer: string[] = [];

    for (const line of inner) {
      const trimmedLine = line.trim();
      if (trimmedLine === '') continue;
      const option = trimmedLine.match(QUIZ_OPTION_RE);
      if (option) {
        quizOptions.push({ right: option[1].toLowerCase() === 'x', text: option[2].trim() });
        continue;
      }
      // Anything that is not an option is explanation copy, so a stray line is
      // shown rather than silently dropped.
      answer.push(trimmedLine);
    }

    // A question with no slug of its own still needs a stable input name.
    const slug = slugifyHeading(question);
    const id = uniqueId(slug ? `quiz-${slug}` : 'quiz');

    let html = '<div class="post-quiz">';
    if (question !== '') {
      countWords(question);
      html += `<p class="post-quiz__question">${renderInline(question, options)}</p>`;
    }
    if (quizOptions.length > 0) {
      html += '<ul class="post-quiz__options">';
      quizOptions.forEach((option, index) => {
        const verdict = option.right ? 'right' : 'wrong';
        const optionId = `${id}-${index + 1}`;
        countWords(option.text);
        html +=
          `<li class="post-quiz__option post-quiz__option--${verdict}">` +
          `<input class="post-quiz__input" type="radio" name="${id}" id="${optionId}">` +
          `<label class="post-quiz__label" for="${optionId}">${renderInline(option.text, options)}</label>` +
          '<span class="post-quiz__mark" aria-hidden="true"></span></li>';
      });
      html += '</ul>';
    }
    if (answer.length > 0) {
      const text = answer.join(' ');
      countWords(text);
      html += `<p class="post-quiz__answer">${renderInline(text, options)}</p>`;
    }
    return `${html}</div>`;
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === '') {
      i += 1;
      continue;
    }

    // Fenced code
    const fence = trimmed.match(/^```(\w*)\s*$/);
    if (fence) {
      const code: string[] = [];
      i += 1;
      while (i < lines.length && !/^```\s*$/.test(lines[i].trim())) {
        code.push(lines[i]);
        i += 1;
      }
      i += 1; // closing fence
      const lang = fence[1] ? ` class="language-${escapeHtml(fence[1])}"` : '';
      emitBlock(`<pre class="post-code"><code${lang}>${escapeHtml(code.join('\n'))}</code></pre>`);
      continue;
    }

    // Callout container
    const callout = trimmed.match(CALLOUT_KIND_RE);
    if (callout) {
      const kind = callout[1] as keyof CalloutLabels;
      const inner: string[] = [];
      i += 1;
      while (i < lines.length && lines[i].trim() !== ':::') {
        inner.push(lines[i]);
        i += 1;
      }
      i += 1; // closing :::
      const label = options.calloutLabels[kind];
      emitBlock(
        `<aside class="post-callout post-callout--${kind}">` +
          `<span class="post-callout__label">${escapeHtml(label)}</span>` +
          `<div class="post-callout__body">${renderCalloutBody(inner)}</div></aside>`,
      );
      continue;
    }

    // Engagement blocks: toggle, quiz, infographic.
    const engagement = trimmed.match(ENGAGEMENT_RE);
    if (engagement) {
      const kind = engagement[1];
      const title = engagement[2].trim();
      const inner: string[] = [];
      i += 1;
      while (i < lines.length && lines[i].trim() !== ':::') {
        inner.push(lines[i]);
        i += 1;
      }
      i += 1; // closing :::

      if (kind === 'toggle') {
        // <details>/<summary>: the disclosure the platform gives for free, so
        // the page stays scripted by nothing at all.
        countWords(title);
        emitBlock(
          '<details class="post-toggle">' +
            `<summary class="post-toggle__summary">${renderInline(title, options)}</summary>` +
            `<div class="post-toggle__body">${renderCalloutBody(inner)}</div></details>`,
        );
      } else if (kind === 'quiz') {
        emitBlock(renderQuiz(title, inner));
      } else {
        // The SVG is emitted live only when every element and attribute is on
        // the infographic allowlist; otherwise the whole block renders escaped,
        // exactly like an unsafe raw-HTML block.
        const svg = inner.join('\n').trim();
        if (!isSvgSafe(svg)) {
          emitBlock(`<p>${escapeHtml(svg)}</p>`);
        } else {
          let caption = '';
          if (title !== '') {
            caption = `<figcaption>${renderInline(title, options)}</figcaption>`;
            countWords(title);
          }
          emitBlock(`<figure class="post-infographic">${svg}${caption}</figure>`);
        }
      }
      continue;
    }

    // Heading
    const heading = trimmed.match(/^(#{2,6})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      const text = heading[2].trim();
      const id = uniqueId(slugifyHeading(text));
      if (level <= 3) headings.push({ id, text });
      countWords(text);
      emitBlock(`<h${level} id="${id}">${renderInline(text, options)}</h${level}>`);
      i += 1;
      continue;
    }

    // Horizontal rule
    if (/^---+$/.test(trimmed)) {
      emitBlock('<hr>');
      i += 1;
      continue;
    }

    // Raw HTML passthrough (until blank line), mirroring the wl engine's
    // author-trust model, behind a TAG ALLOWLIST for the studio: a block whose
    // tags, attributes and URLs are all allowlisted renders live, anything else
    // renders escaped (this feeds dangerouslySetInnerHTML).
    if (trimmed.startsWith('<')) {
      const raw: string[] = [];
      while (i < lines.length && lines[i].trim() !== '') {
        raw.push(lines[i]);
        i += 1;
      }
      const block = raw.join('\n');
      if (isRawHtmlSafe(block)) {
        emitBlock(block);
      } else {
        emitBlock(`<p>${escapeHtml(block)}</p>`);
      }
      continue;
    }

    // Standalone figure: ![alt](src "caption")
    const figure = trimmed.match(FIGURE_RE);
    if (figure) {
      const [, alt, src, caption] = figure;
      const captionHtml = caption ? `<figcaption>${renderInline(caption, options)}</figcaption>` : '';
      emitBlock(
        `<figure class="post-figure"><img src="${escapeHtml(safeUrl(src))}" alt="${escapeHtml(alt)}" loading="lazy">${captionHtml}</figure>`,
      );
      if (caption) countWords(caption);
      i += 1;
      continue;
    }

    // Table: header row + |---| separator
    if (trimmed.startsWith('|') && i + 1 < lines.length && /^\|?[\s:|-]+\|?$/.test(lines[i + 1].trim()) && lines[i + 1].includes('-')) {
      const parseRow = (row: string): string[] =>
        row
          .trim()
          .replace(/^\||\|$/g, '')
          .split('|')
          .map((cell) => cell.trim());
      const headerCells = parseRow(trimmed);
      i += 2;
      const bodyRows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        bodyRows.push(parseRow(lines[i]));
        i += 1;
      }
      headerCells.forEach(countWords);
      bodyRows.forEach((row) => row.forEach(countWords));
      const thead = `<thead><tr>${headerCells.map((cell) => `<th>${renderInline(cell, options)}</th>`).join('')}</tr></thead>`;
      const tbody = `<tbody>${bodyRows
        .map((row) => `<tr>${row.map((cell) => `<td>${renderInline(cell, options)}</td>`).join('')}</tr>`)
        .join('')}</tbody>`;
      emitBlock(`<div class="data-table-wrap"><table class="data-table">${thead}${tbody}</table></div>`);
      continue;
    }

    // Lists (one nesting level, 2-space indent)
    const listMatch = trimmed.match(/^([-*]|\d+\.)\s+/);
    if (listMatch) {
      const ordered = /^\d+\./.test(trimmed);
      const items: ListItem[] = [];
      while (i < lines.length) {
        const current = lines[i];
        const currentTrim = current.trim();
        const isTop = /^([-*]|\d+\.)\s+/.test(currentTrim) && !/^\s{2,}/.test(current);
        const isNested = /^\s{2,}([-*]|\d+\.)\s+/.test(current);
        // A top-level marker of the OTHER kind ends this list and starts a new one.
        if (isTop && /^\d+\./.test(currentTrim) !== ordered) break;
        if (isTop) {
          items.push({ text: currentTrim.replace(/^([-*]|\d+\.)\s+/, ''), children: [] });
        } else if (isNested && items.length > 0) {
          items[items.length - 1].children.push({
            text: currentTrim.replace(/^([-*]|\d+\.)\s+/, ''),
            children: [],
          });
        } else {
          break;
        }
        i += 1;
      }
      const collect = (list: ListItem[]): void =>
        list.forEach((item) => {
          countWords(item.text);
          collect(item.children);
        });
      collect(items);
      if (items.length === 0) {
        // Defensive, mirroring the paragraph branch: the inner loop can break
        // without consuming anything (an indented list marker with no preceding
        // top-level item), and `i` would be unchanged — the outer loop would
        // then re-enter this same branch on the same line forever. This runs
        // during render, so the hang has no promise boundary to escape through.
        // Render the orphan line as a paragraph instead of an empty list.
        const orphan = lines[i].trim();
        countWords(orphan);
        emitBlock(`<p>${renderInline(orphan, options)}</p>`);
        i += 1;
        continue;
      }
      emitBlock(renderList(items, ordered));
      continue;
    }

    // Blockquote: blank quoted lines split paragraphs (wl block-parses quotes).
    if (trimmed.startsWith('>')) {
      const paragraphs: string[][] = [[]];
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        const inner = lines[i].trim().replace(/^>\s?/, '');
        if (inner.trim() === '') paragraphs.push([]);
        else paragraphs[paragraphs.length - 1].push(inner);
        i += 1;
      }
      const html = paragraphs
        .filter((p) => p.length > 0)
        .map((p) => {
          const textQ = p.join(' ');
          countWords(textQ);
          return `<p>${renderInline(textQ, options)}</p>`;
        })
        .join('');
      emitBlock(`<blockquote class="post-quote">${html}</blockquote>`);
      continue;
    }

    // Paragraph: consecutive non-blank, non-structural lines
    const para: string[] = [];
    while (i < lines.length) {
      const currentTrim = lines[i].trim();
      if (
        currentTrim === '' ||
        /^(#{2,6})\s/.test(currentTrim) ||
        currentTrim.startsWith('```') ||
        CALLOUT_KIND_RE.test(currentTrim) ||
        ENGAGEMENT_RE.test(currentTrim) ||
        currentTrim === ':::' ||
        currentTrim.startsWith('|') ||
        currentTrim.startsWith('>') ||
        /^([-*]|\d+\.)\s+/.test(currentTrim) ||
        /^---+$/.test(currentTrim) ||
        currentTrim.startsWith('<') ||
        FIGURE_RE.test(currentTrim)
      ) {
        break;
      }
      para.push(currentTrim);
      i += 1;
    }
    if (para.length > 0) {
      const textP = para.join(' ');
      countWords(textP);
      emitBlock(`<p>${renderInline(textP, options)}</p>`);
    } else {
      // Defensive: a structural line that reached the paragraph branch (e.g. a
      // stray ':::') is consumed so the loop always advances.
      i += 1;
    }
  }

  return { html: out.join('\n'), headings, wordCount };
};

export default renderArticleBody;
